import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Paper,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import api from "../../api/axiosInstance";
import { useNavigate } from 'react-router-dom';
import { useProfile } from "../../providers/ProfileProvider";

const FILE_CONSTRAINTS = {
  MAX_SIZE: 3 * 1024 * 1024,
  ALLOWED_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  ACCEPT_ATTR: '.pdf,.doc,.docx'
};

const useDragAndDrop = (onFileSelect, disabled = false) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && (e.type === 'dragenter' || e.type === 'dragover')) {
      setDragActive(true);
    } else {
      setDragActive(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !e.dataTransfer.files?.[0]) return;
    setDragActive(false);
    onFileSelect(e.dataTransfer.files[0]);
  }, [disabled, onFileSelect]);

  return {
    dragActive,
    dragHandlers: {
      onDragEnter: handleDrag,
      onDragLeave: handleDrag,
      onDragOver: handleDrag,
      onDrop: handleDrop
    }
  };
};

const fileUtils = {
  validate: (file) => {
    if (!file) return 'No file provided';
    if (!(file instanceof File)) return 'Invalid file object';
    if (file.size > FILE_CONSTRAINTS.MAX_SIZE) return 'File size must be less than 3MB';
    if (!FILE_CONSTRAINTS.ALLOWED_TYPES.includes(file.type)) return 'Only PDF and Word documents are allowed';
    if (file.size === 0) return 'File is empty';
    return null;
  },

  formatSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  },

  createFileObject: (file) => {
    if (!(file instanceof File)) throw new Error('Invalid file object');
    return {
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      id: `${file.name}-${file.size}-${Date.now()}`
    };
  },

  getFileExtension: (filename) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  },

  getMimeTypeFromExtension: (filename) => {
    const ext = fileUtils.getFileExtension(filename);
    const mimeTypes = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return mimeTypes[ext] || 'application/octet-stream';
  }
};

const apiService = {
  uploadFiles: async (files, userId) => {
    if (!files || files.length === 0) throw new Error('No files provided for upload');

    const fileObj = files[0];
    if (!fileObj || !fileObj.file) throw new Error('Invalid file object - missing file property');
    if (!(fileObj.file instanceof File)) throw new Error('Invalid file type - not a File object');
    if (fileObj.file.size === 0) throw new Error('File is empty');

    const formData = new FormData();
    formData.append('file', fileObj.file, fileObj.file.name);
    formData.append('fileType', 'cv');
    formData.append('userId', userId);    

    try {
      const response = await api.post('/files/upload', formData, {
        timeout: 30000,
        maxContentLength: 10 * 1024 * 1024,
        maxBodyLength: 10 * 1024 * 1024
      });

      return response.data;
    } catch (error) {
      const status = error.response?.status;
      switch (status) {
        case 401: throw new Error('Your session has expired. Please log in again.');
        case 403: throw new Error('You do not have permission to upload files. Please contact support.');
        case 400: throw new Error(error.response?.data?.message || 'Bad request - please check your file and try again.');
        case 404: throw new Error('Upload endpoint not found. Please contact support.');
        case 413: throw new Error('File is too large. Please use a file smaller than 3MB.');
        case 500: throw new Error('Server error. Please try again in a few moments.');
        default: throw new Error(`Upload failed: ${error.message || 'An unknown error occurred'}`);
      }
    }
  },

  fetchUserCV: async () => {
    try {
      const response = await api.get('/files');

      const files = response.data?.data;
      if (!files || files.length === 0) return null;

      const cvFiles = files.filter(file => file.fileType === 'cv');
      if (cvFiles.length === 0) return null;

      const mostRecentCV = cvFiles.sort((a, b) =>
        new Date(b.uploadedAt) - new Date(a.uploadedAt)
      )[0];

      return {
        name: mostRecentCV.originalName,
        size: mostRecentCV.size || 0,
        type: fileUtils.getMimeTypeFromExtension(mostRecentCV.originalName),
        uploadDate: mostRecentCV.uploadedAt,
        filePath: mostRecentCV.url,
        record: mostRecentCV,
        uploaded: true,
        id: mostRecentCV._id
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
};

const FileUploadZone = ({
  uploadedFiles, uploadedCVData, dragActive, dragHandlers, onBrowseClick, fileInputRef
}) => {
  const hasFiles = uploadedFiles.length > 0 || uploadedCVData;
  const isDisabled = hasFiles;

  return (
    <Paper
      sx={{
        border: hasFiles
          ? '2px solid #E84610'
          : dragActive ? '2px solid #E84610' : '2px dashed #d0d0d0',
        borderRadius: 2,
        p: 6,
        textAlign: 'center',
        backgroundColor: hasFiles 
          ? '#EAE7E6'
          : dragActive ? '#fff3f0' : '#fafafa',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        mb: 2,
        opacity: isDisabled ? 0.6 : 1,
        '&:hover': !isDisabled ? { borderColor: '#E84610', backgroundColor: '#fff8f5' } : {}
      }}
      {...(!isDisabled && dragHandlers)}
      onClick={!isDisabled ? onBrowseClick : undefined}
    >
      <CloudUploadIcon sx={{ fontSize: 48, color: isDisabled ? '#E84610' : '#2196F3', mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: isDisabled ? '#E84610' : '#333' }}>
        {hasFiles ? 'CV uploaded successfully!' : <>
          <Link component="span" sx={{ color: '#E84610', textDecoration: 'underline', cursor: 'pointer', mr: 0.5 }}>
            Click to upload
          </Link> or drag and drop
        </>}
      </Typography>
      <Typography variant="body2" sx={{ color: isDisabled ? '#000000' : '#666', fontSize: '14px' }}>
        {hasFiles ? 'Remove file below to upload a different one' : 'PDF, Microsoft Word (max. 3MB) - Optional'}
      </Typography>

      <input ref={fileInputRef} type="file" accept={FILE_CONSTRAINTS.ACCEPT_ATTR} style={{ display: 'none' }} disabled={isDisabled} />
    </Paper>
  );
};

const UploadedFileDisplay = ({ file, onRemove, isFromServer = false }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', fontWeight: 500 }}>
      Uploaded CV File
    </Typography>
    <Paper sx={{ border: '2px solid #E84610', borderRadius: 2, p: 3, backgroundColor: '#EAE7E6' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FileIcon sx={{ color: '#E84610', fontSize: 32 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#333', mb: 0.5 }}>
              {file.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              {fileUtils.formatSize(file.size)} • {isFromServer ? 'Previously uploaded' : 'Ready to upload'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onRemove}
          sx={{ color: '#f44336', '&:hover': { backgroundColor: '#ffebee' } }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Paper>
  </Box>
);

const FormActions = ({ onBack, isSubmitting }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    mt: 2
  }}>
    <Button 
      variant="outlined" 
      startIcon={<ArrowBackIcon />} 
      onClick={onBack} 
      disabled={isSubmitting}
      size="small"
    >
      Back
    </Button>

    <Button 
      type="submit" 
      variant="contained" 
      disabled={isSubmitting} 
      size="small" 
      sx={{ px: 3 }}
    >
      {isSubmitting ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> 
          Uploading...
        </>
      ) : 'Next'}
    </Button>
  </Box>
);

const UploadCV = ({ formData, setFormData, onNext, onBack }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const user = useProfile();
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCV, setIsLoadingCV] = useState(true);

  const [uploadedCVData, setUploadedCVData] = useState(null);

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);


  useEffect(() => {
    const fetchExistingCV = async () => {
      if (!user?._id) {
        setIsLoadingCV(false);
        return;
      }

      if (formData.cvFile?.uploaded) {
        setUploadedCVData(formData.cvFile);
        setIsLoadingCV(false);
        return;
      }

      try {
        const cvData = await apiService.fetchUserCV();
        
        if (cvData) {
          setUploadedCVData(cvData);
          updateFormData('cvFile', cvData);
        } else {
          console.log('No CV found on server');
        }
      } catch (error) {
        // Don't show error to user - missing CV is not an error
      } finally {
        setIsLoadingCV(false);
      }
    };

    fetchExistingCV();
  }, [user?._id, formData.cvFile, updateFormData]);

  const handleFileSelect = useCallback((file) => {
    setUploadError('');
    if (uploadedFiles.length > 0 || uploadedCVData) {
      setUploadError('Only one CV file can be uploaded. Please remove the current file to upload a new one.');
      return;
    }
    
    const validationError = fileUtils.validate(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    try {
      const newFile = fileUtils.createFileObject(file);
      setUploadedFiles([newFile]);
      updateFormData('cvFile', newFile);
    } catch (error) {
      setUploadError(`Error processing file: ${error.message}`);
    }
  }, [uploadedFiles.length, uploadedCVData, updateFormData]);

  const handleRemoveFile = useCallback(() => {
    setUploadedFiles([]);
    setUploadedCVData(null);
    updateFormData('cvFile', null);
    setUploadError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [updateFormData]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  }, [handleFileSelect]);

  const { dragActive, dragHandlers } = useDragAndDrop(handleFileSelect, uploadedFiles.length > 0 || uploadedCVData);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // If there's already an uploaded CV (from server), just continue
    if (uploadedCVData) {
      onNext();
      return;
    }
    
    // If no files uploaded, skip upload and continue
    if (uploadedFiles.length === 0) {
      updateFormData('cvFile', null);
      onNext();
      return;
    }

    // If files are selected, validate and upload them
    const fileObj = uploadedFiles[0];
    if (!fileObj || !fileObj.file || !(fileObj.file instanceof File)) {
      setUploadError('Invalid file detected. Please remove and re-upload your CV.');
      return;
    }

    setIsSubmitting(true);
    setSaveStatus(null);
    setUploadError('');
    
    try {
      const result = await apiService.uploadFiles(uploadedFiles, user._id);
      const savedFile = result.data;
      const uploadedData = {
        name: savedFile.originalName,
        size: savedFile.size,
        type: savedFile.mimetype,
        uploadDate: savedFile.uploadedAt,
        filePath: savedFile.url,
        record: savedFile,
        uploaded: true,
        id: savedFile._id
      };
      
      setUploadedCVData(uploadedData);
      updateFormData('cvFile', uploadedData);
      setUploadedFiles([]);
      
      setTimeout(() => {
        onNext();
      }, 500);
      
    } catch (error) {
      console.error('❌ Upload error:', error);
      setSaveStatus({ 
        type: 'error', 
        message: error.message || 'Failed to upload CV. Please try again.',
        retry: () => {
          setSaveStatus(null);
          handleSubmit(e);
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [uploadedFiles, uploadedCVData, updateFormData, onNext, user._id]);

  if (isLoadingCV) {
    return (
      <Box sx={{ 
        minHeight: 'calc(100vh - 116px)',
        bgcolor: '#EAE7E6',
        pt: 2,
        px: '80px',
        pb: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 116px)',
      bgcolor: '#EAE7E6',
      pt: 2,
      px: '80px',
      pb: 3
    }}>
      <Container maxWidth={false} sx={{ px: 0, height: '100%' }}>
        <Box sx={{ 
          p: 4, 
          bgcolor: 'white',
          minHeight: 'calc(100vh - 116px - 32px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Upload Your CV
            </Typography>
            <Typography variant="body2">
              Please upload your CV to continue (optional)
            </Typography>
          </Box>

          {saveStatus && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Alert 
                severity={saveStatus.type} 
                sx={{ width: '512px' }}
                onClose={() => setSaveStatus(null)}
                action={
                  saveStatus.type === 'error' && saveStatus.retry ? (
                    <Button color="inherit" size="small" onClick={saveStatus.retry}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {saveStatus.message}
              </Alert>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}>
            <Box sx={{
              width: '512px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <form
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px'
                }}
              >
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <FileUploadZone
                    uploadedFiles={uploadedFiles}
                    uploadedCVData={uploadedCVData}
                    dragActive={dragActive}
                    dragHandlers={dragHandlers}
                    onBrowseClick={handleBrowseClick}
                    fileInputRef={fileInputRef}
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_CONSTRAINTS.ACCEPT_ATTR}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                    disabled={uploadedFiles.length > 0 || uploadedCVData}
                  />

                  {(uploadedFiles.length > 0 || uploadedCVData) && (
                    <UploadedFileDisplay
                      file={uploadedCVData || uploadedFiles[0]}
                      onRemove={handleRemoveFile}
                      isFromServer={!!uploadedCVData}
                    />
                  )}

                  {uploadError && (
                    <Alert 
                      severity="error" 
                      sx={{ mt: 2, fontSize: '14px' }}
                    >
                      {uploadError}
                    </Alert>
                  )}
                  
                  {uploadedFiles.length === 0 && !uploadedCVData && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 2,
                        color: '#666',
                        fontSize: '14px',
                        textAlign: 'center',
                        fontStyle: 'italic'
                      }}
                    >
                      You can skip this step and upload your CV later
                    </Typography>
                  )}
                </Box>

                <FormActions
                  onBack={onBack}
                  isSubmitting={isSubmitting}
                />
              </form>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default UploadCV;